import { App as AntdApp } from "antd";
import { BrowserRouter } from "react-router-dom";
import React, { useEffect, Suspense, useMemo } from "react";
import {
  ApolloClient,
  InMemoryCache,
  ApolloProvider,
  HttpLink,
  from,
} from "@apollo/client";
import { ErrorBoundary } from "./components/error-boundary";
import { onError } from "@apollo/client/link/error";
import { Provider as ReduxProvider } from "react-redux";
import { store } from "./store/store";
import AlertManager from "./components/AlertManager";
import { setContext } from "@apollo/client/link/context";
import { config } from "./config/env";
import { AuthProvider } from "./contexts/AuthContext";
import { LocationProvider } from "./contexts/LocationContext";
import { AuthenticatedAppContent } from "./components/AuthenticatedAppContent";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import "./styles/sidebar.css";

const errorLink = onError(({ graphQLErrors, networkError }) => {
  if (graphQLErrors) {
    graphQLErrors.forEach(({ message, locations, path }) => {
      console.error(
        `[GraphQL error]: Message: ${message}, Location: ${locations}, Path: ${path}`,
      );
    });
  }
  if (networkError) console.error(`[Network error]: ${networkError}`);
});

function App() {
  useEffect(() => {
    document.title = "Expedient";
  }, []);

  const queryClient = useMemo(() => new QueryClient({
    defaultOptions: {
      queries: {
        staleTime: 1000 * 60 * 5, // 5 minutes
        retry: 1,
      },
    },
  }), []);

  const client = useMemo(() => {
    // Use NestJS GraphQL endpoint for energy/weather queries
    // Hasura queries use separate client in providers/data/index.tsx
    const httpLink = new HttpLink({
      uri: config.nestjsGraphqlUrl || config.apiUrl + "/api/graphql",
    });

    const authLink = setContext((_, { headers }) => {
      const token = localStorage.getItem("access_token");
      return {
        headers: {
          ...headers,
          authorization: token ? `Bearer ${token}` : "",
        },
      };
    });

    return new ApolloClient({
      link: from([errorLink, authLink.concat(httpLink)]),
      cache: new InMemoryCache(),
      defaultOptions: {
        watchQuery: {
          fetchPolicy: "cache-and-network",
        },
      },
      connectToDevTools: import.meta.env.DEV,
    });
  }, []);

  return (
    <ErrorBoundary>
      <ReduxProvider store={store}>
        <ApolloProvider client={client}>
          <BrowserRouter>
            <AuthProvider>
              <LocationProvider>
                <QueryClientProvider client={queryClient}>
                  <AntdApp>
                    <Suspense fallback={<div>Loading...</div>}>
                      <AuthenticatedAppContent />
                    </Suspense>
                    <AlertManager />
                  </AntdApp>
                </QueryClientProvider>
              </LocationProvider>
            </AuthProvider>
          </BrowserRouter>
        </ApolloProvider>
      </ReduxProvider>
    </ErrorBoundary>
  );
}

export default App;
